import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation, TFunction } from 'react-i18next';
import { differenceInMinutes } from 'date-fns';
import { CHAT_BOT } from '__queries/query-keys';
import { ChatBot } from 'ts-components/ChatBot';
import { MessageType } from 'ts-components/Chat';
import MachineLearningService from 'services/MachineLearningService';
import { useModalControl } from 'hooks/useModalControl';
import {
  SAMPLE_IDEAS,
  TASK_TYPE,
  SESSION_RESUME_THRESHOLD_WITH_FLOW,
  SESSION_RESUME_THRESHOLD_WITHOUT_FLOW,
} from './constants';
import { PageWrapper } from './styles';
import { QuickResponseData } from 'ts-components/QuickResponses';
import { OutputVariableRequirements } from 'ts-components/ChatBot/types';
import { useChatBotEventStream } from 'ts-components/Conversation/useChatBotEventStream';
import { useGenerationFlowEventStream } from 'ts-components/Conversation/useGenerationFlowEventStream';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { wysiwygCopyLocalStorage } from 'storage/components';
import { useSessionHistory } from './hooks/useSessionHistory';
import { SessionHistoryPanel } from './components/SessionHistoryPanel';
import { ChatSessionHistoryItem } from './types';
import { deferExecution } from 'utility/defer';
import { getChosenBusinessEntitlement } from 'store/authentication/selectors';

const CHAT_BUTTONS = {
  REVIEW_PLAN: {
    buttonText: (t: TFunction) => t('Review AI Plan'),
    message: (entity: string | undefined) =>
      `With the information already provided, generate my ${entity?.toLocaleLowerCase() ?? ''} requirements`,
  },
  START_OVER: {
    buttonText: (t: TFunction) => t('New Chat'),
    message: 'Start Over',
  },
  REFINE_PLAN: {
    buttonText: (t: TFunction) => t('Refine Plan'),
    message: (entity: string | undefined) =>
      `Ask more questions to refine my ${entity?.toLocaleLowerCase() ?? ''} generation plan`,
  },
};

const UnifiedChatEmbed = ({
  onSwitchToAIChat,
}: {
  onSwitchToAIChat?: () => void;
} = {}) => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const hasAiGraphPreview = useSelector((state: any) =>
    getChosenBusinessEntitlement(state, 'ai_graph_preview')
  );

  const queryParams = new URLSearchParams(location.search);
  const headerLogoUrl = queryParams.get('header-logo-url');
  const headerText = queryParams.get('header') || undefined;

  const [chatSessionId, setChatSessionId] = useState('');
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  const [isReadOnlySession, setIsReadOnlySession] = useState(false);
  const [hasInitializedSession, setHasInitializedSession] = useState(false);

  const [
    isHistoryPanelOpen,
    { showModal: openHistoryPanel, hideModal: closeHistoryPanel },
  ] = useModalControl(false);

  const {
    sessions,
    latestSession,
    isLoading: isHistoryLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    deleteSession,
    refetch: refetchHistory,
  } = useSessionHistory(TASK_TYPE);

  const { data: modelConfigMetadata } = useQuery({
    queryKey: CHAT_BOT.METADATA,
    queryFn: async () => {
      return MachineLearningService.getChatMetadata();
    },
    enabled: true,
  });

  const streamEventListeners = useMemo(() => {
    const eventListenerNames =
      modelConfigMetadata?.[TASK_TYPE]?.output_variables?.map(
        ({ name }: OutputVariableRequirements) => name
      ) ?? [];
    return eventListenerNames;
  }, [modelConfigMetadata]);

  const { sendMessageWithEventStream, abortStream } = useChatBotEventStream({
    chatSessionId,
    eventListenerNames: streamEventListeners,
  });

  const { startFlowStream } = useGenerationFlowEventStream({
    chatSessionId,
    importResults: true,
  });

  const shouldResumeSession = useMemo(() => {
    if (!latestSession) return false;

    const isActiveStatus = ['pending_prompt', 'processing_prompt'].includes(
      latestSession.status
    );
    if (!isActiveStatus) return false;

    const minutesSinceLastActivity = differenceInMinutes(
      new Date(),
      new Date(latestSession.last_message_datetime)
    );

    const threshold = latestSession.last_flow
      ? SESSION_RESUME_THRESHOLD_WITH_FLOW
      : SESSION_RESUME_THRESHOLD_WITHOUT_FLOW;

    return minutesSinceLastActivity < threshold;
  }, [latestSession]);

  // Initialize session based on history
  useEffect(() => {
    if (hasInitializedSession || isHistoryLoading) return;

    if (shouldResumeSession && latestSession) {
      // Resume existing session
      setChatSessionId(latestSession.id);
      setIsReadOnlySession(false);
      setHasInitializedSession(true);
    }
  }, [
    hasInitializedSession,
    isHistoryLoading,
    shouldResumeSession,
    latestSession,
  ]);

  const createNewSession = useCallback(async () => {
    try {
      setIsSessionLoading(true);
      setSessionError('');
      setIsReadOnlySession(false);
      const session = await MachineLearningService.createNewChatSession(
        TASK_TYPE,
        {}
      );
      setChatSessionId(session.id);
      setIsSessionLoading(false);
      setHasInitializedSession(true);
      return session;
    } catch (error: any) {
      setIsSessionLoading(false);
      const errorMessage =
        error?.response?.data?.message || t('Failed to create chat session');
      setSessionError(errorMessage);
      throw error;
    }
  }, [t]);

  const { isLoading: isInitialLoading, refetch: refetchSession } = useQuery({
    queryKey: CHAT_BOT.NEW_CHAT_SESSION(TASK_TYPE, {}),
    queryFn: createNewSession,
    retry: false,
    throwOnError: false,
    enabled:
      !isHistoryLoading && !shouldResumeSession && !hasInitializedSession,
  });

  const handleFetchSession = useCallback(async () => {
    if (chatSessionId) {
      return await MachineLearningService.getChatHistory(chatSessionId);
    }
    return { messages: [] };
  }, [chatSessionId]);

  const handleCopyMessage = useCallback(async (messageContent: string) => {
    // check if in iframe to post message to window parent
    if (window.self === window.top) {
      try {
        await navigator.clipboard.writeText(messageContent);
        wysiwygCopyLocalStorage.clear();
      } catch (error) {
        monitoringExceptionHelper(error);
      }
    } else {
      window.parent?.postMessage(
        {
          source: 'kizen-ai-chat-embed',
          event: { type: 'copyToClipboard', messageContent },
        },
        '*'
      );
    }
  }, []);

  const handleLikeMessage = useCallback(async () => {
    try {
      await MachineLearningService.createChatFlag({
        chatId: chatSessionId,
        tag: 'user_positive',
        reason: 'User liked the message',
        options: {},
      });
    } catch (error) {
      monitoringExceptionHelper(error);
    }
  }, [chatSessionId]);

  const handleDislikeMessage = useCallback(async () => {
    try {
      await MachineLearningService.createChatFlag({
        chatId: chatSessionId,
        tag: 'user_negative',
        reason: 'User disliked the message',
        options: {},
      });
    } catch (error) {
      monitoringExceptionHelper(error);
    }
  }, [chatSessionId]);

  const handleRefreshMessage = useCallback(async () => {
    try {
      // Remove the last system message before retrying
      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(chatSessionId),
        (prev: { messages: MessageType[] } = { messages: [] }) => {
          const messages = [...prev.messages];
          // Remove all messages sent prior to last sent message
          for (let i = messages.length - 1; i >= 0; i--) {
            if (!messages[i]?.isUserMessage) {
              messages.splice(i, 1);
            } else {
              break;
            }
          }
          return {
            ...prev,
            messages,
          };
        }
      );

      const endpointConfig = {
        getStreamingEndpoint: MachineLearningService.getStreamingEndpoint,
        getRetryStreamingEndpoint:
          MachineLearningService.getRetryStreamingEndpoint,
      };

      await sendMessageWithEventStream('', endpointConfig, chatSessionId, true);
    } catch (error) {
      monitoringExceptionHelper(error);
    }
  }, [queryClient, chatSessionId, sendMessageWithEventStream]);

  const handleCompleteSession = useCallback(
    async (sessionId: string) => {
      await startFlowStream(
        sessionId,
        () => setIsBuilding(true),
        () => setIsBuilding(false)
      );
    },
    [startFlowStream]
  );

  const handleDeleteSession = useCallback(async () => {
    abortStream();

    if (chatSessionId) {
      try {
        setChatSessionId('');
        setIsReadOnlySession(false);
        setHasInitializedSession(false);
        refetchHistory();
        deferExecution(() => refetchSession());
      } catch (error) {
        monitoringExceptionHelper(error);
      }
    }
  }, [chatSessionId, refetchHistory, refetchSession, abortStream]);

  const handleSelectSession = useCallback(
    (session: ChatSessionHistoryItem) => {
      closeHistoryPanel();
      setChatSessionId(session.id);

      const isReadOnly =
        session.status === 'complete' || session.status === 'canceled';
      setIsReadOnlySession(isReadOnly);

      queryClient.invalidateQueries({
        queryKey: CHAT_BOT.CHAT_SESSION(session.id),
      });
    },
    [closeHistoryPanel, queryClient]
  );

  const handleDeleteHistorySession = useCallback(
    async (sessionId: string) => {
      await deleteSession(sessionId);

      // If deleted current session, create a new one
      if (sessionId === chatSessionId) {
        setChatSessionId('');
        setIsReadOnlySession(false);
        setHasInitializedSession(false);
        refetchSession();
      }
    },
    [deleteSession, chatSessionId, refetchSession]
  );

  const sampleIdeas = useMemo(() => {
    const ideas = SAMPLE_IDEAS(t);
    return {
      options: ideas.sort(() => 0.5 - Math.random()).slice(0, 4),
      selectType: 'single_select',
    } as QuickResponseData;
  }, [t]);

  const headerMenuOptions = useMemo(
    () => [
      { value: 'new_chat', label: t('New Chat') },
      { value: 'session_history', label: t('Session History') },
      ...(hasAiGraphPreview || onSwitchToAIChat
        ? [
            {
              value: 'switch_to_ai_chat',
              label: t('Custom Object Agent'),
            },
          ]
        : []),
      { value: 'close', label: t('Close') },
    ],
    [t, hasAiGraphPreview, onSwitchToAIChat]
  );

  const handleHeaderMenuOptionSelect = useCallback(
    ({ value }: { value: string }) => {
      if (value === 'session_history') {
        refetchHistory();
        openHistoryPanel();
      } else if (value === 'new_chat') {
        handleDeleteSession();
      } else if (value === 'switch_to_ai_chat') {
        if (onSwitchToAIChat) {
          onSwitchToAIChat();
        } else {
          history.push('/embed/ai-chat');
        }
      } else if (value === 'close') {
        window.parent?.postMessage(
          {
            source: 'kizen-ai-chat-embed',
            event: { type: 'closeAiChat' },
          },
          '*'
        );
      }
    },
    [
      refetchHistory,
      openHistoryPanel,
      handleDeleteSession,
      history,
      onSwitchToAIChat,
    ]
  );

  const readOnlyPlaceholder = isReadOnlySession
    ? t('This session is read-only')
    : undefined;

  return (
    <PageWrapper>
      <div className="relative w-full h-full">
        {isHistoryPanelOpen ? (
          <SessionHistoryPanel
            sessions={sessions}
            currentSessionId={chatSessionId}
            isLoading={isHistoryLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            onClose={closeHistoryPanel}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteHistorySession}
            headerLogoUrl={headerLogoUrl}
          />
        ) : (
          <ChatBot
            showLogo
            headerLogoUrl={headerLogoUrl}
            header={headerText || t('Kizen AI (Beta)')}
            chatButtons={CHAT_BUTTONS}
            entityName={''}
            chatSessionId={chatSessionId}
            fetchSession={handleFetchSession}
            onCompleteSession={handleCompleteSession}
            getStreamingEndpoint={(id) =>
              MachineLearningService.getStreamingEndpoint(id)
            }
            renderMarkdown
            onDeleteSession={handleDeleteSession}
            errorMessage={sessionError}
            eventListenerNames={streamEventListeners}
            sampleIdeas={isReadOnlySession ? undefined : sampleIdeas}
            newChatSessionLoading={
              isSessionLoading || isInitialLoading || isHistoryLoading
            }
            enableQuickActionButtons
            hideQuickActionsButtons={isBuilding || isReadOnlySession}
            hideRefresh={isBuilding || isReadOnlySession}
            disabledSend={isBuilding || isReadOnlySession}
            headerMenuOptions={headerMenuOptions}
            handleHeaderMenuOptionSelect={handleHeaderMenuOptionSelect}
            onCopyMessage={handleCopyMessage}
            onLikeMessage={handleLikeMessage}
            onDislikeMessage={handleDislikeMessage}
            onRefreshMessage={
              isReadOnlySession ? undefined : handleRefreshMessage
            }
            placeholderOverride={readOnlyPlaceholder}
            disableTextAreaMessage={readOnlyPlaceholder}
          />
        )}
      </div>
    </PageWrapper>
  );
};

export default UnifiedChatEmbed;
